import React, { useState, useEffect, useCallback } from 'react';
import { Header } from './layout/Header';
import { Sidebar } from './layout/Sidebar';
import { Breadcrumb } from './ui/Breadcrumb';
import { LoadingScreen } from './ui/LoadingScreen';
import { Calculator } from './calculator/Calculator';
import { ChatPanel } from './chat/ChatPanel';
import { ChatContainer } from './chat/ChatContainer';
import { useCalculatorStore } from '../hooks/useCalculatorStore';
import { useChatStore } from '../hooks/useChatStore';
import { useLocation } from 'react-router-dom';
import { NetworkQualityService } from '../services/performance/NetworkQualityService';
import type { ConnectionQuality } from '../services/performance/NetworkQualityService';

interface LayoutProps {
  children: React.ReactNode;
}

interface LoadingStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'completed' | 'error';
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>('good');
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>([
    { id: 'auth', label: 'Vérification de l\'authentification', status: 'pending' },
    { id: 'config', label: 'Chargement de la configuration', status: 'pending' },
    { id: 'data', label: 'Préparation des données', status: 'pending' },
    { id: 'ui', label: 'Initialisation de l\'interface', status: 'pending' },
  ]);
  
  const isCalculatorOpen = useCalculatorStore(state => state.isOpen);
  const { displayMode, panelWidth } = useChatStore();
  const location = useLocation();

  // Le chat est affiché en panel si on n'est pas sur la page /chat
  // Sur mobile, forcer le mode fullscreen (pas de split-view)
  const showChatPanel = displayMode === 'panel' && location.pathname !== '/chat' && !isMobile;
  const showChatFullscreen = (displayMode === 'fullscreen' || (displayMode === 'panel' && isMobile)) && location.pathname !== '/chat';

  // Mettre à jour une étape de chargement
  const updateStep = useCallback((stepId: string, status: LoadingStep['status']) => {
    setLoadingSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status } : step
    ));
  }, []);

  // Écouter la qualité de connexion
  useEffect(() => {
    const unsubscribe = NetworkQualityService.subscribe((info) => {
      setConnectionQuality(info.quality);
    });
    return unsubscribe;
  }, []);

  // Simulation du chargement progressif
  useEffect(() => {
    const simulateLoading = async () => {
      const quality = NetworkQualityService.getQuality();
      const delays = {
        excellent: { base: 200, step: 150 },
        good: { base: 300, step: 200 },
        moderate: { base: 500, step: 300 },
        slow: { base: 800, step: 500 },
        offline: { base: 1000, step: 600 }
      };
      
      const { base, step } = delays[quality] || delays.good;

      // Étape 1: Auth
      updateStep('auth', 'loading');
      setLoadingProgress(10);
      await new Promise(r => setTimeout(r, base));
      updateStep('auth', 'completed');
      setLoadingProgress(25);

      // Étape 2: Config
      updateStep('config', 'loading');
      await new Promise(r => setTimeout(r, step));
      updateStep('config', 'completed');
      setLoadingProgress(50);

      // Étape 3: Data
      updateStep('data', 'loading');
      await new Promise(r => setTimeout(r, step));
      updateStep('data', 'completed');
      setLoadingProgress(75);

      // Étape 4: UI
      updateStep('ui', 'loading');
      await new Promise(r => setTimeout(r, step));
      updateStep('ui', 'completed');
      setLoadingProgress(100);

      // Petit délai avant de masquer l'écran
      await new Promise(r => setTimeout(r, 200));
      setIsLoading(false);
    };

    simulateLoading();
  }, [updateStep]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
    if (isMobile) {
      document.body.style.overflow = !sidebarOpen ? 'hidden' : 'auto';
    }
  };

  if (isLoading) {
    return (
      <LoadingScreen 
        showProgress={true}
        progress={loadingProgress}
        steps={loadingSteps}
        connectionQuality={connectionQuality}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <Header onToggleSidebar={handleSidebarToggle} />
      
      <div className="flex pt-16">
        {/* Sidebar - Se réduit quand le chat est ouvert */}
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => isMobile && setSidebarOpen(false)}
          collapsed={showChatPanel && !isMobile}
        />
        
        {isMobile && sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-10 animate-fade-in"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Zone principale avec split-view */}
        <div className="flex-1 flex h-[calc(100vh-4rem)] overflow-hidden">
          {/* Contenu principal */}
          <main 
            className={`
              flex-1 p-4 md:p-6 overflow-auto
              transition-all duration-300
              ${isMobile && sidebarOpen ? 'blur-sm' : ''}
            `}
            style={{
              width: showChatPanel ? `${100 - panelWidth}%` : '100%'
            }}
          >
            <div className="max-w-7xl mx-auto space-y-4 animate-fade-in-up">
              <Breadcrumb />
              {children}
            </div>
          </main>

          {/* Panel Chat - Côte à côte */}
          {showChatPanel && (
            <ChatPanel />
          )}
        </div>
      </div>

      {/* Fullscreen Chat Overlay (mobile or explicit fullscreen) */}
      {showChatFullscreen && (
        <div className="fixed inset-0 z-50 bg-background">
          <ChatContainer 
            mode="fullscreen"
            onClose={() => useChatStore.getState().closeChat()}
            onModeChange={() => useChatStore.getState().collapseToPanel()}
          />
        </div>
      )}

      {isCalculatorOpen && <Calculator />}
    </div>
  );
}